import { useEffect, useRef, useState } from 'react';
import { Text } from '@noorden-insurtech/alba-design-system';
import { useRdIndemnityPeriods } from '@/features/quotes/hooks/rd-equipamentos/use-rd-indemnity-periods.hook';
import { useSetRdCoverages } from '@/features/quotes/hooks/rd-equipamentos/use-set-rd-coverages.hook';
import { brlInputToCents } from '@/features/quotes/utils/rd-units.util';
import type {
  RdCoverageDef,
  RdModalityCoverages,
  RdProposalEquipment,
} from '@/services/quotes/rd-equipamentos/types';
import { ValidationError } from '@/shared/exceptions/validation.error';
import { toaster } from '@/shared/ui/toaster.component';
import { useDebouncedValue } from '@/shared/utils/use-filters.hook';
import {
  applyIndemnityChange,
  applyLmiChange,
  applyToggle,
  BASICA_REQUIRED_MESSAGE,
  buildInitialRows,
  buildPayload,
  isBasicaInvalid,
  isOverLimit,
  resolveCoverageFieldRowIndex,
  type CoverageRow,
} from './coverage-rows.util';
import { CoverageRowsTable } from './coverage-rows-table.component';

const AUTOSAVE_DEBOUNCE_MS = 500;

export type CoverageSaveStatus =
  | 'idle'
  | 'saving'
  | 'saved'
  | 'error'
  | 'invalid';

type CoverageMatrixProps = {
  proposalId: string;
  item: RdProposalEquipment;
  rateGrid: RdModalityCoverages;
  coverages: RdCoverageDef[];
  disabled?: boolean;
  // eslint-disable-next-line no-unused-vars -- nome dos parâmetros documenta o contrato da callback
  onStatusChange: (itemId: string, status: CoverageSaveStatus) => void;
};

const STATUS_COLOR_CLASS: Record<CoverageSaveStatus, string> = {
  idle: 'text-alba-gray-500',
  saving: 'text-alba-gray-500',
  saved: 'text-alba-success-green-500',
  error: 'text-alba-error-red-500',
  invalid: 'text-alba-error-red-500',
};

/**
 * Matriz de coberturas de um item JÁ adicionado à proposta (Step 2, fluxo
 * individual) — a tabela em si é `CoverageRowsTable`; este componente é o
 * wrapper que dá a ela persistência automática.
 *
 * Autosave: qualquer edição (checkbox/LMI) dispara, após 500ms sem novas
 * edições, um PUT da matriz completa via `useSetRdCoverages`. O array
 * enviado preserva a ordem das linhas — necessário para mapear o erro de
 * campo `coverages.<index>.lmi` de volta à linha correta.
 *
 * Guard contra loop de autosave: o estado inicial de `rows` é lido uma
 * única vez (`useState(() => buildInitialRows(...))`) a partir de
 * `item.coverages` — o refetch de `['rd-proposal', proposalId]` que segue
 * um save bem-sucedido troca a referência da prop `item`, mas nunca
 * realimenta `rows` (não há efeito de sincronização a partir de `item`).
 * Um `isMountRef` pula o primeiro disparo do efeito debounced (montagem) e
 * uma comparação com o último payload salvo evita reenviar um payload
 * idêntico.
 *
 * Sinal síncrono de "sujo": como o efeito debounced só marca `'saving'`
 * depois dos 500ms sem edições, existia uma janela em que o usuário edita e
 * clica em "Próximo" antes do debounce disparar — o pai navega, este
 * componente desmonta, o cleanup do `useDebouncedValue` cancela o
 * `setTimeout` pendente e a edição nunca é salva. Por isso `handleToggle` e
 * `handleLmiChange` chamam `onStatusChange(item.itemId, 'saving')` (e
 * espelham em `status` local) de forma SÍNCRONA, no próprio handler —
 * antes do debounce — apenas para avisar o pai; isso nunca dispara `save`
 * nem realimenta `rows`, então não reintroduz o loop de autosave. O efeito
 * debounced permanece a única fonte de verdade para transições para
 * `'saved'`/`'error'`; ele também limpa o sinal síncrono (volta a `'idle'`)
 * quando a edição é revertida antes do debounce e o payload resultante
 * coincide com o último salvo — sem isso, "Próximo" ficaria bloqueado para
 * sempre nesse caso.
 *
 * C4 — validação da Básica: derivada separadamente do autosave, via
 * `isBasicaInvalid` (linha `mandatory` com LMI 0/vazio). `displayStatus`
 * sobrepõe `'invalid'` ao `status` do autosave sempre que ela é verdadeira —
 * assim o `useEffect` abaixo reporta `'invalid'` ao pai pelo MESMO canal
 * (`onStatusChange`) já usado por `'saving'`/`'error'`, e o step só precisa
 * somar `'invalid'` ao gate que já bloqueia "Próximo". `'invalid'` é
 * distinto de `'error'` (que continua significando "o PUT falhou") para não
 * rotular erroneamente um item ainda intocado como "erro ao salvar".
 */
export function CoverageMatrix({
  proposalId,
  item,
  rateGrid,
  coverages,
  disabled = false,
  onStatusChange,
}: CoverageMatrixProps) {
  const [rows, setRows] = useState<CoverageRow[]>(() =>
    buildInitialRows(item, rateGrid, coverages)
  );
  const [status, setStatus] = useState<CoverageSaveStatus>('idle');
  const [rowErrors, setRowErrors] = useState<Record<string, string>>({});
  const [accordionError, setAccordionError] = useState<string | undefined>();

  const { save } = useSetRdCoverages({ proposalId, itemId: item.itemId });
  // Cadastro de produto, cacheado 5 min — o mesmo para todas as linhas.
  const { data: indemnityPeriods } = useRdIndemnityPeriods();

  const isMountRef = useRef(true);
  const lastSavedPayloadRef = useRef<string>(
    JSON.stringify(buildPayload(rows))
  );

  const debouncedRows = useDebouncedValue(rows, AUTOSAVE_DEBOUNCE_MS);

  useEffect(() => {
    if (isMountRef.current) {
      isMountRef.current = false;
      return;
    }
    if (disabled) return;

    // Sem a Básica preenchida não adianta salvar: os limites das demais
    // coberturas são relativos a ela (`<=basica`), então o backend recusa
    // com 400 e o usuário vê "I.S. acima do limite" por ter preenchido numa
    // ordem perfeitamente legítima — Danos Elétricos antes da Básica. O
    // estado não se perde: `basicaInvalid` já reporta 'invalid' ao pai e
    // bloqueia o "Próximo" (fix C4), e o save acontece assim que a Básica
    // recebe um valor.
    const basicaRowToSave = debouncedRows.find(row => row.mandatory);
    if (
      basicaRowToSave !== undefined &&
      brlInputToCents(basicaRowToSave.lmiDisplay) <= 0
    ) {
      return;
    }

    // Linha acima do teto: o PUT sairia só para voltar 400. O erro já está
    // na tela (validação de cliente) e o "Próximo" já está bloqueado; salvar
    // aqui só trocaria a mensagem por uma igual, vinda de mais longe.
    if (isOverLimit(debouncedRows)) return;

    const payload = buildPayload(debouncedRows);
    const snapshot = JSON.stringify(payload);
    if (snapshot === lastSavedPayloadRef.current) {
      // Edição foi revertida antes do debounce disparar (ex.: toggle e
      // toggle de volta) — limpa o sinal síncrono de "sujo" marcado pelos
      // handlers, senão "Próximo" ficaria bloqueado para sempre sem nunca
      // haver um save de fato em andamento.
      setStatus('idle');
      onStatusChange(item.itemId, 'idle');
      return;
    }

    let cancelled = false;

    async function run() {
      setStatus('saving');
      setRowErrors({});
      setAccordionError(undefined);
      onStatusChange(item.itemId, 'saving');

      try {
        await save(payload);
        if (cancelled) return;
        lastSavedPayloadRef.current = snapshot;
        setStatus('saved');
        onStatusChange(item.itemId, 'saved');
      } catch (error) {
        if (cancelled) return;

        if (error instanceof ValidationError && error.fieldErrors) {
          const nextRowErrors: Record<string, string> = {};
          const unmatchedMessages: string[] = [];

          Object.entries(error.fieldErrors).forEach(([field, fieldError]) => {
            const rowIndex = resolveCoverageFieldRowIndex(field, debouncedRows);
            const slug =
              rowIndex !== null
                ? debouncedRows[rowIndex]?.coverageSlug
                : undefined;
            if (slug) {
              nextRowErrors[slug] = fieldError.message;
            } else if (fieldError.message) {
              // Chave não mapeável para nenhuma linha (índice fora da
              // matriz, ou formato inesperado) — não pode ser descartada
              // silenciosamente, então cai no banner de erro do accordion.
              unmatchedMessages.push(fieldError.message);
            }
          });

          setRowErrors(nextRowErrors);
          if (unmatchedMessages.length > 0) {
            setAccordionError(unmatchedMessages.join('\n'));
          }
        } else {
          setAccordionError(
            error instanceof Error ? error.message : 'Erro inesperado'
          );
        }

        setStatus('error');
        onStatusChange(item.itemId, 'error');
        toaster.create({
          type: 'error',
          title: 'Não foi possível salvar as coberturas',
          description:
            error instanceof Error ? error.message : 'Erro inesperado',
        });
      }
    }

    void run();

    return () => {
      cancelled = true;
    };
  }, [debouncedRows, disabled, item.itemId, onStatusChange, save]);

  // C4: Básica (`mandatory`) exige LMI > 0 — checado a cada render a partir
  // de `rows` (não debounced: o gate de "Próximo" deve reagir no instante da
  // digitação, igual ao sinal síncrono de "sujo" do autosave).
  //
  // Inalcançável desde que a Básica passou a ser derivada de `item.value`
  // (o motor exige `value >= 1` em `add_equipment`); mantido como guarda
  // defensiva contra dados antigos ou um `value` zerado vindo de fora.
  const basicaInvalid = isBasicaInvalid(rows);
  // Teto de cobertura estourado também segura o "Próximo": antes o corretor
  // só descobria pelo 400 do motor, depois do autosave — e o gate deixava
  // avançar enquanto o PUT falhava em segundo plano.
  const overLimit = isOverLimit(rows);
  const displayStatus: CoverageSaveStatus =
    basicaInvalid || overLimit ? 'invalid' : status;

  useEffect(() => {
    onStatusChange(item.itemId, displayStatus);
  }, [displayStatus, item.itemId, onStatusChange]);

  function handleToggle(slug: string) {
    if (disabled) return;
    const next = applyToggle(rows, slug);
    // `applyToggle` devolve o MESMO array quando a linha é obrigatória
    // (Básica) — nesse caso não há edição, e marcar "sujo" bloquearia o
    // "Próximo" sem nunca haver um save para desbloqueá-lo.
    if (next === rows) return;
    // Sinal síncrono: marca "sujo" no pai imediatamente, antes do debounce,
    // para que "Próximo" bloqueie desde o instante da edição (ver doc do
    // componente). Não dispara save nem realimenta `rows`.
    setStatus('saving');
    onStatusChange(item.itemId, 'saving');
    setRows(next);
  }

  function handleLmiChange(slug: string, raw: string) {
    if (disabled) return;
    // Mesmo sinal síncrono de "sujo" que `handleToggle` — ver doc do
    // componente.
    setStatus('saving');
    onStatusChange(item.itemId, 'saving');
    setRows(prev => applyLmiChange(prev, slug, raw));
  }

  /**
   * O período escolhido entra no MESMO estado das linhas, então segue pelo
   * mesmo autosave: o motor apaga e reinsere as linhas do item a cada PUT, e
   * um caminho separado perderia o prazo no salvamento seguinte.
   */
  function handleIndemnityChange(slug: string, months: number) {
    if (disabled) return;
    setStatus('saving');
    onStatusChange(item.itemId, 'saving');
    setRows(prev => applyIndemnityChange(prev, slug, months));
  }

  const statusLabel: Record<CoverageSaveStatus, string> = {
    idle: 'As alterações são salvas automaticamente.',
    saving: 'salvando…',
    saved: 'salvo',
    error: accordionError || 'erro ao salvar',
    invalid: BASICA_REQUIRED_MESSAGE,
  };

  return (
    <div className='flex flex-col gap-3'>
      <CoverageRowsTable
        rows={rows}
        rowErrors={rowErrors}
        basicaInvalid={basicaInvalid}
        disabled={disabled}
        onToggle={handleToggle}
        onLmiChange={handleLmiChange}
        indemnityPeriods={indemnityPeriods}
        onIndemnityChange={handleIndemnityChange}
      />

      <Text className={`text-xs ${STATUS_COLOR_CLASS[displayStatus]}`}>
        {statusLabel[displayStatus]}
      </Text>
    </div>
  );
}
